import { createClient } from '@supabase/supabase-js';
import { fixtureOn, fixtureRpc } from './fixture';

/**
 * Il client Supabase della console.
 *
 * È l'unico canale verso il server: non esiste un backend della console. Tutta
 * l'autorizzazione sta nelle funzioni `security definer` della migration
 * 20260812180000 e nella RLS delle tabelle base (attiva, zero policy). Questo
 * bundle può contenere solo la publishable key proprio perché non c'è niente,
 * qui dentro, di cui fidarsi.
 */

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

/**
 * Manca la configurazione: `null` invece di un client rotto.
 *
 * Un client costruito su una stringa vuota fallisce più tardi, con un errore di
 * rete incomprensibile su una schermata già disegnata. Meglio saperlo al primo
 * render e dirlo: è esattamente il caso in cui un deploy senza variabili
 * d'ambiente somiglia a un bug del codice.
 */
export const isConfigured = Boolean(url && key);

export const supabase = isConfigured
  ? createClient(url!, key!, {
      auth: {
        /* Resta acceso per i link della vecchia forma (`?code=`, PKCE) rimasti
           in casella: l'SDK li consuma da sé prima che React monti. I link
           nuovi NON passano di qui — arrivano come `?token_hash=…`, che
           `detectSessionInUrl` non riconosce, e li spende `AuthProvider`. */
        detectSessionInUrl: true,
        persistSession: true,
        autoRefreshToken: true,
        /* NON PKCE dal 19/08/2026, e la ragione nasce sull'app: il template
           dell'email è UNO SOLO per tutto il progetto, e per far aprire l'app
           dal tap è stato riscritto con `{{ .TokenHash }}` al posto di
           `{{ .ConfirmationURL }}` (vedi `am-mobile/src/lib/supabase.ts`).
           Quel token si spende con `verifyOtp`, che pretende una sessione dalla
           `POST /verify`: coniato in PKCE tornerebbe un `code`, e la console
           resterebbe fuori senza un errore da mostrare.

           `{{ .RedirectTo }}` nel template tiene separate le due destinazioni:
           qui arriva `siteUrl()`, all'app il suo Universal Link. */
        flowType: 'implicit',
      },
    })
  : null;

/** Dove Supabase rimanda dopo il magic link. */
export function siteUrl(): string {
  const configured = import.meta.env.VITE_SITE_URL as string | undefined;
  return (configured || window.location.origin).replace(/\/+$/, '');
}

/**
 * Chiamata a una RPC, con l'errore già tradotto.
 *
 * Le funzioni sollevano eccezioni con un prefisso a codice
 * (`AUTH_REQUIRED:`, `NOT_MEMBER:`, `SUSPENDED:`) perché il messaggio grezzo di
 * Postgres non è una cosa da mostrare a un operatore comunale alle tre di notte.
 */
export async function rpc<T>(
  fn: string,
  args: Record<string, unknown> = {},
): Promise<T> {
  if (fixtureOn) {
    const canned = fixtureRpc(fn, args);
    if (canned !== undefined) return canned as T;
  }
  if (!supabase) throw new RpcError('CONFIG', 'Console non configurata.');

  const { data, error } = await supabase.rpc(fn, args);
  if (!error) return data as T;

  // Il token di accesso dura un'ora, e comporre un avviso può durare di più:
  // chi scrive alle tre di notte lo fa lentamente, e rilegge. Con la sessione
  // scaduta ogni RPC torna 401 — la funzione non viene nemmeno eseguita.
  //
  // Prima si rinfresca e si riprova UNA volta. Non è una gentilezza: senza,
  // l'unico modo di sbloccare la console è ricaricare la pagina, e ricaricare
  // costa la bozza. Un solo tentativo, perché se il refresh non basta il
  // problema è la sessione e va detto, non nascosto sotto altri tentativi.
  if (isAuthError(error)) {
    const { error: refreshError } = await supabase.auth.refreshSession();
    if (!refreshError) {
      const retry = await supabase.rpc(fn, args);
      if (!retry.error) return retry.data as T;
      throw fromPostgrest(retry.error.message);
    }
    throw new RpcError(
      'SESSION_EXPIRED',
      'La sessione è scaduta e non sono riuscito a rinnovarla. Rientra dal link che ti abbiamo mandato: quello che hai scritto resta dov’è.',
    );
  }

  throw fromPostgrest(error.message);
}

/** Errori che significano «il token non vale più», non «la richiesta è sbagliata». */
function isAuthError(error: { code?: string; message?: string }): boolean {
  const code = String(error.code ?? '');
  const msg = String(error.message ?? '');
  // PGRST301 = JWT scaduto o non valido; PGRST303 = claim mancante.
  return (
    code === 'PGRST301' ||
    code === 'PGRST303' ||
    code === '401' ||
    /jwt (expired|invalid)|invalid token|not authenticated/i.test(msg) ||
    // Le nostre funzioni sollevano questo quando auth.uid() è null: dal lato
    // client è indistinguibile da un token scaduto, ed è la stessa cura.
    /^AUTH_REQUIRED:/.test(msg)
  );
}

export class RpcError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'RpcError';
    this.code = code;
  }
}

const MESSAGES: Record<string, string> = {
  AUTH_REQUIRED: 'La sessione è scaduta. Rientra dal link che ti abbiamo mandato.',
  NOT_MEMBER:
    'Questo account non è abilitato a pubblicare per nessun Comune. Se dovrebbe esserlo, scrivi a chi ti ha dato l’accesso.',
};

function fromPostgrest(raw: string): RpcError {
  const match = /^([A-Z_]+):\s*(.*)$/.exec(raw.trim());
  if (!match) return new RpcError('UNKNOWN', raw);
  const code = match[1] ?? 'UNKNOWN';
  const detail = match[2] ?? '';
  // SUSPENDED porta con sé il motivo scritto da chi ha sospeso l'ente: è più
  // utile di qualunque testo generico, quindi si mostra quello.
  return new RpcError(code, MESSAGES[code] ?? (detail || raw));
}
